import React, { useState, useEffect } from "react";
import StandardModal from "../shared/Modal/StandardModal";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import "./EditScalesModal.css";

function EditScalesModal({ isOpen, onClose, rep, scaleType, onUpdate }) {
  const { getVerifiedJWT } = useAuth();
  const [scaleLines, setScaleLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Map scale type to display name and data attribute
  const scaleConfig = {
    commission_scale: {
      title: "Commission Scale",
      apiAttribute: "commission_scale",
      dataKey: "commission_scale_lines",
    },
    override_scale: {
      title: "Override Scale",
      apiAttribute: "override_scale",
      dataKey: "override_scale_lines",
    },
    marketing_deal_scale: {
      title: "Marketing Deal Scale",
      apiAttribute: "marketing_deal_scale",
      dataKey: "md_scale_lines",
    },
  };

  const config = scaleConfig[scaleType];

  useEffect(() => {
    if (isOpen && rep && config) {
      const existingLines = rep[config.dataKey] || [];
      if (existingLines.length > 0) {
        setScaleLines(
          existingLines.map((line, index) => ({
            id: index + 1,
            revenue_amount: line.revenue_amount || "",
            percentage: line.percentage || "",
          }))
        );
      } else {
        // Start with one empty row if no existing scale
        setScaleLines([{ id: 1, revenue_amount: "", percentage: "" }]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, rep, scaleType]);

  const handleScaleChange = (id, field, value) => {
    setScaleLines(prev =>
      prev.map(item => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const addRow = () => {
    const newId =
      scaleLines.length > 0
        ? Math.max(...scaleLines.map(item => item.id)) + 1
        : 1;
    setScaleLines(prev => [
      ...prev,
      {
        id: newId,
        revenue_amount: "",
        percentage: "",
      },
    ]);
  };

  const removeRow = id => {
    if (scaleLines.length > 1) {
      setScaleLines(prev => prev.filter(item => item.id !== id));
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setError("Authentication failed");
        return;
      }

      // Filter out empty rows and process the data
      const validScales = scaleLines.filter(
        scale => scale.revenue_amount || scale.percentage
      );

      if (validScales.length === 0) {
        setError("Please add at least one scale entry");
        setLoading(false);
        return;
      }

      const processedScaleLines = validScales.map(scale => ({
        revenue: parseFloat(scale.revenue_amount) || 0,
        percentage: parseFloat(scale.percentage) || 0,
      }));

      const requestBody = {
        [config.apiAttribute]: processedScaleLines,
      };

      const response = await fetch(
        getApiEndpoint(`/v1/rep/details/${rep.rep_id}`),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (response.ok) {
        // Pass the saved lines back so the parent can update its state in-place
        onUpdate(
          processedScaleLines.map(l => ({
            revenue_amount: l.revenue,
            percentage: l.percentage,
          }))
        );
        onClose();
      } else {
        const data = await response.json();
        setError(data.message || `Failed to update ${config.title}`);
      }
    } catch (err) {
      setError(`Failed to update ${config.title}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      setError(null);

      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setError("Authentication failed");
        return;
      }

      const requestBody = {
        [config.apiAttribute]: [],
      };

      const response = await fetch(
        getApiEndpoint(`/v1/rep/details/${rep.rep_id}`),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (response.ok) {
        onUpdate([]); // Pass empty array so parent clears the scale in-place
        onClose();
      } else {
        const data = await response.json();
        setError(data.message || `Failed to delete ${config.title}`);
      }
    } catch (err) {
      setError(`Failed to delete ${config.title}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    onClose();
  };

  if (!config) return null;

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={handleCancel}
      title={`Edit ${config.title}`}
      error={error}
      footerLeft={
        <button
          className="btn btn-sm btn-danger"
          onClick={handleDelete}
          disabled={loading}
        >
          Delete Scale
        </button>
      }
      footerRight={
        <>
          <button
            className="btn btn-sm btn-light"
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="btn btn-sm btn-primary"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </>
      }
    >
      <div className="edit-scales-modal">
        <div className="edit-scales-modal-form">
          <p className="edit-scales-modal-description">
            Configure the {config.title.toLowerCase()} for this representative.
            Add or remove rows as needed.
          </p>

          <div className="edit-scales-modal-scale-table-container">
            <table className="edit-scales-modal-scale-table">
              <thead>
                <tr>
                  <th>Percentage (%)</th>
                  <th>Revenue ($)</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {scaleLines.map(item => (
                  <tr key={item.id}>
                    <td>
                      <input
                        type="number"
                        value={item.percentage}
                        onChange={e =>
                          handleScaleChange(
                            item.id,
                            "percentage",
                            e.target.value
                          )
                        }
                        placeholder="0.00"
                        min="0"
                        max="100"
                        step="0.01"
                        disabled={loading}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        value={item.revenue_amount}
                        onChange={e =>
                          handleScaleChange(
                            item.id,
                            "revenue_amount",
                            e.target.value
                          )
                        }
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        disabled={loading}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="app-scale-remove-row-button"
                        onClick={() => removeRow(item.id)}
                        disabled={scaleLines.length <= 1 || loading}
                        title="Remove row"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="edit-scales-modal-table-actions">
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={addRow}
              disabled={loading}
            >
              <FontAwesomeIcon icon={faPlus} /> Add Row
            </button>
          </div>
        </div>
      </div>
    </StandardModal>
  );
}

export default EditScalesModal;
